import { Inject, Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions } from "@microsoft/signalr";
import { lastValueFrom } from "rxjs";
import { MessageService } from "primeng/api";
import { AUTH_SCOPES } from "./injection-tokens";

export abstract class SignalingServiceBase
{    
    abstract startHub(hub: HubConnection): Promise<void>
    abstract generateHub(hubUri:string) : HubConnection
}

@Injectable({providedIn: 'root'})
export class SignalingService extends SignalingServiceBase
{
    constructor
    (
        private msalService: MsalService,
        private _messages: MessageService,
        @Inject(AUTH_SCOPES) private _authScopes: string[]
    )
    {
        super();
    }

    async startHub(hub: HubConnection): Promise<void>
    {
        if (hub.state === HubConnectionState.Disconnected)
        {
            try
            {
                await hub.start();
            }
            catch (err)
            {
                console.error('Hub start failed', err);
            }
        }
    }

    override generateHub(hubUri: string): HubConnection
    {
        try
        {

            let messagingHub = new HubConnectionBuilder()
            .withAutomaticReconnect([5000,10000])
            .withUrl(hubUri,this.hubOptions)
            .build();


            messagingHub.onreconnecting
            (
                e => 
                    {
                        console.log("Reconnecting... " + hubUri);
                        this._messages.add({ key: 'main-alert', life: 10, sticky: false, severity: 'error', summary: 'OFFLINE', detail: 'API is offline. Attempting Reconnect'});
                    }
            );

            messagingHub.onreconnected
            (
                connectionId => 
                    {
                        console.log("Successfully Reconnected " + hubUri);
                        this._messages.add({ key: 'main-alert', life: 10, sticky: false, severity: 'success', summary: 'ONLINE', detail: 'API is back online!'});

                    }
            );

            messagingHub.onclose
            (
                error =>
                {
                    console.log(`Connection closed: ${error ? 'with error ' : ''}` + hubUri, error);
                    //try to reconnect after a delay in case of transient issue (automatic reconnect only tries 2x over 15s)
                    //this will help if the system is extremely slow, Azure SignalR restarts, etc
                    //and might alleviate the "first sign on" issue where it seems SignalR never fully connects
                    //since all of the hubs now check for user and close if there is none, this will NOT
                    //re-open legitimately closed hubs
                    setTimeout(()=>this.startHub(messagingHub), 15000);
                }
            )

            return messagingHub;
        }
        catch(error)
        {
            console.error("Error creating hub connection: " + error);
            throw error;
        }
    }

    private hubOptions: IHttpConnectionOptions = 
    {
        withCredentials: true,
        accessTokenFactory: async () => 
        {
            var account = this.msalService.instance.getAllAccounts()[0];
            var client = this.msalService.acquireTokenSilent
            (
                {
                    scopes: this._authScopes,
                    account: account
                }
            )
            var token = (await lastValueFrom(client)).accessToken.replaceAll("'","");
            return token;
        }
    };

}